import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  FaRobot, FaPaperPlane, FaTrash, FaCopy, FaCheck, FaLightbulb, 
  FaCalendarAlt, FaEnvelope, FaChartLine, FaMagic, FaUser, FaImage,
  FaBirthdayCake, FaUserPlus, FaMapMarkerAlt, FaUsers, FaDownload,
  FaFileImage, FaTimes, FaWhatsapp, FaFilePdf, FaFileWord, FaFileExcel, 
  FaVideo, FaPaperclip, FaFileAlt, FaGoogleDrive, FaSearch, FaFont
} from 'react-icons/fa';

interface AttachedFile {
  id: string;
  url: string;
  name: string;
  type: 'image' | 'video' | 'pdf' | 'doc' | 'excel' | 'file';
  role?: 'MEMBER' | 'SEPARATE';
  extension?: string;
}

interface ChatMessage {
  id: string;
  sender: 'user' | 'assistant';
  text: string;
  image?: string;
  images?: string[];
  attachedFiles?: AttachedFile[];
  graphicData?: {
    type: 'BIRTHDAY' | 'WELCOME' | 'FIELD_VISIT' | 'MEETING';
    title: string;
    memberName?: string;
    company?: string;
    designation?: string;
    date?: string;
    venue?: string;
    photoUrl?: string;
    details?: string;
    backgroundStyle?: 'ROYAL_GOLD_BEAMS' | 'DEEP_NAVY_LUXURY' | 'JAIPUR_ARCHITECTURAL' | 'EMERALD_ELEGANCE' | 'CUSTOM_IMAGE';
    bgImageUrl?: string;
  };
  timestamp: string;
}

interface MemberOption {
  id: number;
  name: string;
  email: string;
  company_name?: string;
  designation?: string;
  profile?: string;
  DOB?: string;
}

const normalizePhotoUrl = (rawUrl?: string): string => {
  if (!rawUrl || rawUrl.includes('placeholder.com')) return '';
  let cleaned = rawUrl.trim();
  if (cleaned.startsWith('http://') || cleaned.startsWith('https://') || cleaned.startsWith('data:')) {
    return cleaned;
  }
  if (cleaned.startsWith('/uploads')) {
    return `https://rmbbackend.rmbjaipur.com${cleaned}`;
  }
  return `https://rmbbackend.rmbjaipur.com/uploads/${cleaned.replace(/\\/g, '/')}`;
};

const handleShareWhatsAppGraphic = (graphic: ChatMessage['graphicData']) => {
  if (!graphic) return;
  const text = encodeURIComponent(
    `*${graphic.title}*\n\n` +
    (graphic.memberName ? `👤 *${graphic.memberName}*\n` : '') +
    (graphic.designation ? `💼 ${graphic.designation}\n` : '') +
    (graphic.company ? `🏢 ${graphic.company}\n` : '') +
    (graphic.date ? `📅 ${graphic.date}\n` : '') +
    (graphic.venue ? `📍 ${graphic.venue}\n` : '') +
    `\n_${graphic.details || 'Rotary Means Business Fellowship International'}_`
  );
  window.open(`https://api.whatsapp.com/send?text=${text}`, '_blank');
};

const GraphicPosterCanvas: React.FC<{ 
  graphic: ChatMessage['graphicData'];
  onEditGraphic?: (graphic: ChatMessage['graphicData']) => void;
}> = ({ graphic, onEditGraphic }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!graphic || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const isPortrait = graphic.type === 'MEETING' || graphic.title.includes('Poster');
    const width = 1080;
    const height = isPortrait ? 1350 : 1080;
    canvas.width = width;
    canvas.height = height;

    const renderCanvas = (memberImg?: HTMLImageElement, bgImg?: HTMLImageElement) => {
      // 1. Background Image or Gradient Style
      if (bgImg && bgImg.complete && bgImg.naturalWidth > 0) {
        ctx.drawImage(bgImg, 0, 0, width, height);
        ctx.fillStyle = 'rgba(6, 18, 38, 0.45)';
        ctx.fillRect(0, 0, width, height);
      } else if (graphic.backgroundStyle === 'EMERALD_ELEGANCE') {
        const bgGrad = ctx.createLinearGradient(0, 0, width, height);
        bgGrad.addColorStop(0, '#022C22');
        bgGrad.addColorStop(0.5, '#064E3B');
        bgGrad.addColorStop(1, '#021F17');
        ctx.fillStyle = bgGrad;
        ctx.fillRect(0, 0, width, height);
      } else if (graphic.backgroundStyle === 'DEEP_NAVY_LUXURY') {
        const bgGrad = ctx.createLinearGradient(0, 0, width, height);
        bgGrad.addColorStop(0, '#030A19');
        bgGrad.addColorStop(0.5, '#0A192F');
        bgGrad.addColorStop(1, '#020712');
        ctx.fillStyle = bgGrad;
        ctx.fillRect(0, 0, width, height);
      } else {
        const bgGrad = ctx.createLinearGradient(0, 0, width, height);
        bgGrad.addColorStop(0, '#061226');
        bgGrad.addColorStop(0.35, '#0A2451');
        bgGrad.addColorStop(0.75, '#0B2C66');
        bgGrad.addColorStop(1, '#030A19');
        ctx.fillStyle = bgGrad;
        ctx.fillRect(0, 0, width, height);
      }

      // 2. Gold Ray Flare Overlay
      const beamGrad = ctx.createLinearGradient(0, 0, width, height * 0.6);
      beamGrad.addColorStop(0, 'rgba(253, 188, 18, 0.25)');
      beamGrad.addColorStop(0.5, 'rgba(217, 161, 27, 0.08)');
      beamGrad.addColorStop(1, 'transparent');
      ctx.fillStyle = beamGrad;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(width, 0);
      ctx.lineTo(width, height * 0.45);
      ctx.lineTo(0, height * 0.75);
      ctx.closePath();
      ctx.fill();

      // 3. Golden Outer Framing
      ctx.strokeStyle = '#FDBC12';
      ctx.lineWidth = 14;
      ctx.strokeRect(20, 20, width - 40, height - 40);

      ctx.strokeStyle = 'rgba(217, 161, 27, 0.4)';
      ctx.lineWidth = 3;
      ctx.strokeRect(34, 34, width - 68, height - 68);

      // 4. Header Badge Bar
      ctx.fillStyle = '#FDBC12';
      ctx.fillRect(40, 40, width - 80, 84);

      ctx.font = 'black 34px sans-serif';
      ctx.fillStyle = '#0A2451';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText((graphic.title || 'ROTARY OFFICIAL GRAPHIC').toUpperCase(), width / 2, 82);

      // 5. Member Photo / Avatar Frame
      const photoCenterX = isPortrait ? 810 : 250;
      const photoCenterY = isPortrait ? 520 : 540;
      const radius = 165;

      // Glow & Golden Ring
      ctx.save();
      ctx.shadowColor = 'rgba(253, 188, 18, 0.6)';
      ctx.shadowBlur = 30;
      ctx.strokeStyle = '#FDBC12';
      ctx.lineWidth = 10;
      ctx.beginPath();
      ctx.arc(photoCenterX, photoCenterY, radius + 8, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();

      // Draw Photo or Golden Initials Avatar Badge
      ctx.save();
      ctx.beginPath();
      ctx.arc(photoCenterX, photoCenterY, radius, 0, Math.PI * 2);
      ctx.clip();

      if (memberImg && memberImg.complete && memberImg.naturalWidth > 0) {
        ctx.drawImage(memberImg, photoCenterX - radius, photoCenterY - radius, radius * 2, radius * 2);
      } else {
        const avGrad = ctx.createLinearGradient(photoCenterX - radius, photoCenterY - radius, photoCenterX + radius, photoCenterY + radius);
        avGrad.addColorStop(0, '#131E32');
        avGrad.addColorStop(1, '#0A2451');
        ctx.fillStyle = avGrad;
        ctx.fillRect(photoCenterX - radius, photoCenterY - radius, radius * 2, radius * 2);

        const initials = (graphic.memberName || 'Rotary Member').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() || 'RM';
        ctx.font = 'black 110px sans-serif';
        ctx.fillStyle = '#FDBC12';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(initials, photoCenterX, photoCenterY);
      }
      ctx.restore();

      // 6. Text Section
      const textX = isPortrait ? 70 : 460;
      let textY = isPortrait ? 220 : 340;

      ctx.textAlign = 'left';

      // Member Name
      if (graphic.memberName) {
        ctx.font = 'black 52px sans-serif';
        ctx.fillStyle = '#FFFFFF';
        ctx.fillText(graphic.memberName, textX, textY);
        textY += 65;
      }

      // Designation
      if (graphic.designation) {
        ctx.font = 'bold 32px sans-serif';
        ctx.fillStyle = '#FDBC12';
        ctx.fillText(graphic.designation, textX, textY);
        textY += 50;
      }

      // Company
      if (graphic.company) {
        ctx.font = 'bold 28px sans-serif';
        ctx.fillStyle = '#94A3B8';
        ctx.fillText(`🏢 ${graphic.company}`, textX, textY);
        textY += 60;
      }

      // Wish / Message
      if (graphic.details) {
        ctx.font = 'italic 24px sans-serif';
        ctx.fillStyle = '#E2E8F0';
        ctx.fillText(`"${graphic.details}"`, textX, textY);
      }

      // 7. Info Cards Box (Date, Time, Venue)
      if (graphic.date || graphic.venue) {
        const cardY = isPortrait ? 920 : 760;
        ctx.fillStyle = 'rgba(19, 30, 50, 0.9)';
        ctx.strokeStyle = '#FDBC12';
        ctx.lineWidth = 3;
        
        ctx.beginPath();
        ctx.roundRect(70, cardY, width - 140, 140, 24);
        ctx.fill();
        ctx.stroke();

        ctx.font = 'black 30px sans-serif';
        ctx.fillStyle = '#FDBC12';
        ctx.fillText(`📅 DATE: ${graphic.date || 'Upcoming Rotary Event'}`, 100, cardY + 50);

        if (graphic.venue) {
          ctx.font = 'bold 26px sans-serif';
          ctx.fillStyle = '#FFFFFF';
          ctx.fillText(`📍 VENUE: ${graphic.venue}`, 100, cardY + 100);
        }
      }

      // 8. Footer Motto
      const footerY = height - 55;
      ctx.fillStyle = '#FDBC12';
      ctx.fillRect(70, footerY - 40, width - 140, 2);

      ctx.font = 'bold 24px sans-serif';
      ctx.fillStyle = '#94A3B8';
      ctx.textAlign = 'center';
      ctx.fillText('ROTARY MEANS BUSINESS INTERNATIONAL • SERVICE ABOVE SELF', width / 2, footerY);
    };

    const resolvedPhoto = normalizePhotoUrl(graphic.photoUrl);
    const resolvedBg = normalizePhotoUrl(graphic.bgImageUrl);

    let loadedMemberImg: HTMLImageElement | undefined = undefined;
    let loadedBgImg: HTMLImageElement | undefined = undefined;

    const checkAndRender = () => {
      renderCanvas(loadedMemberImg, loadedBgImg);
    };

    if (!resolvedPhoto && !resolvedBg) {
      renderCanvas();
    } else {
      if (resolvedPhoto) {
        const mImg = new Image();
        mImg.crossOrigin = 'anonymous';
        mImg.onload = () => { loadedMemberImg = mImg; checkAndRender(); };
        mImg.onerror = () => { checkAndRender(); };
        mImg.src = resolvedPhoto;
      }
      if (resolvedBg) {
        const bImg = new Image();
        bImg.crossOrigin = 'anonymous';
        bImg.onload = () => { loadedBgImg = bImg; checkAndRender(); };
        bImg.onerror = () => { checkAndRender(); };
        bImg.src = resolvedBg;
      }
    }
  }, [graphic]);

  const handleDownload = () => {
    if (!canvasRef.current) return;
    const link = document.createElement('a');
    link.download = `rotary_${(graphic?.type || 'poster').toLowerCase()}_${Date.now()}.png`;
    link.href = canvasRef.current.toDataURL('image/png');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!graphic) return null;

  return (
    <div className="space-y-3 bg-[#0B1329] p-4 rounded-2xl border border-amber-400/40 shadow-xl mt-3">
      <div className="flex items-center justify-between border-b border-slate-800 pb-2">
        <span className="bg-amber-400 text-slate-950 text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full tracking-wider flex items-center gap-1">
          <FaFileImage /> {graphic.title}
        </span>
        <span className="text-[10px] text-amber-300 font-extrabold uppercase">Ultra-HD Canvas Graphic</span>
      </div>

      <div className="w-full overflow-hidden rounded-xl border border-amber-500/30 shadow-2xl bg-black flex justify-center p-2">
        <canvas ref={canvasRef} className="max-w-full h-auto rounded-lg shadow-inner cursor-pointer" />
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
        {onEditGraphic && (
          <button
            type="button"
            onClick={() => onEditGraphic(graphic)}
            className="px-3.5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-black rounded-xl text-xs shadow-md transition flex items-center gap-1.5 cursor-pointer"
          >
            ⚙️ Edit Fields & Background
          </button>
        )}
        <button
          type="button"
          onClick={() => handleShareWhatsAppGraphic(graphic)}
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-black rounded-xl text-xs shadow-md transition flex items-center gap-1.5 cursor-pointer"
        >
          <FaWhatsapp size={15} /> Share on WhatsApp
        </button>
        <button
          type="button"
          onClick={handleDownload}
          className="px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-950 font-black rounded-xl text-xs shadow-md transition flex items-center gap-1.5 cursor-pointer"
        >
          <FaDownload /> Download 1080x1080 Poster (PNG)
        </button>
      </div>
    </div>
  );
};

const ChatGPTAdminStudio: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    const saved = localStorage.getItem('RMB_CHATGPT_ADMIN_SESSION_V3');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      } catch (e) {
        console.error('Failed to restore chat session:', e);
      }
    }
    return [
      {
        id: '1',
        sender: 'assistant',
        text: 'Hello Admin! 👋 I am your built-in ChatGPT Graphic & Executive Assistant for Rotary / RMB. Session continuity is active! Navigating between tabs will keep your entire chat history, generated posters, and active prompts perfectly preserved.',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }
    ];
  });
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState('gpt-4o');
  const [showSkillGuideModal, setShowSkillGuideModal] = useState(false);
  const [showLlmModelModal, setShowLlmModelModal] = useState(false);

  // Google Drive Attachment Picker State
  const [showGDrivePickerModal, setShowGDrivePickerModal] = useState(false);
  const [gdriveResources, setGdriveResources] = useState<any[]>([]);
  const [primaryDriveLink, setPrimaryDriveLink] = useState('');
  const [customGDriveUrl, setCustomGDriveUrl] = useState('');

  // Poster Fields & Background Customizer State
  const [showPosterCustomizerModal, setShowPosterCustomizerModal] = useState(false);
  const [editingMsgId, setEditingMsgId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editMemberName, setEditMemberName] = useState('');
  const [editDesignation, setEditDesignation] = useState('');
  const [editCompany, setEditCompany] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editVenue, setEditVenue] = useState('');
  const [editDetails, setEditDetails] = useState('');
  const [editPhotoUrl, setEditPhotoUrl] = useState('');
  const [editBackgroundStyle, setEditBackgroundStyle] = useState<string>('ROYAL_GOLD_BEAMS');
  const [editBgImageUrl, setEditBgImageUrl] = useState('');

  const handleOpenPosterCustomizer = (msgId: string, graphic: ChatMessage['graphicData']) => {
    if (!graphic) return;
    setEditingMsgId(msgId);
    setEditTitle(graphic.title || '');
    setEditMemberName(graphic.memberName || '');
    setEditDesignation(graphic.designation || '');
    setEditCompany(graphic.company || '');
    setEditDate(graphic.date || '');
    setEditVenue(graphic.venue || '');
    setEditDetails(graphic.details || '');
    setEditPhotoUrl(graphic.photoUrl || '');
    setEditBackgroundStyle(graphic.backgroundStyle || 'ROYAL_GOLD_BEAMS');
    setEditBgImageUrl(graphic.bgImageUrl || '');
    setShowPosterCustomizerModal(true);
  };

  const handleSavePosterCustomizer = () => {
    if (!editingMsgId) return;
    setMessages(prev => prev.map(m => {
      if (m.id === editingMsgId && m.graphicData) {
        return {
          ...m,
          graphicData: {
            ...m.graphicData,
            title: editTitle,
            memberName: editMemberName,
            designation: editDesignation,
            company: editCompany,
            date: editDate,
            venue: editVenue,
            details: editDetails,
            photoUrl: editPhotoUrl,
            backgroundStyle: editBackgroundStyle as any,
            bgImageUrl: editBgImageUrl
          }
        };
      }
      return m;
    }));
    setShowPosterCustomizerModal(false);
    setEditingMsgId(null);
  };

  // AI OCR Image Text & Font Extractor Component State
  const [showOcrModal, setShowOcrModal] = useState(false);
  const [isExtractingOcr, setIsExtractingOcr] = useState(false);
  const [ocrImageTarget, setOcrImageTarget] = useState('');
  const [ocrTitle, setOcrTitle] = useState('');
  const [ocrMemberName, setOcrMemberName] = useState('');
  const [ocrDesignation, setOcrDesignation] = useState('');
  const [ocrCompany, setOcrCompany] = useState('');
  const [ocrDate, setOcrDate] = useState('');
  const [ocrVenue, setOcrVenue] = useState('');
  const [ocrDetails, setOcrDetails] = useState('');
  const [ocrFontFamily, setOcrFontFamily] = useState('Bold Corporate Sans-Serif');
  const [ocrRawText, setOcrRawText] = useState('');

  const handleExtractTextFromImage = async (imageUrl: string) => {
    if (!imageUrl) return;
    setOcrImageTarget(imageUrl);
    setIsExtractingOcr(true);
    setShowOcrModal(true);

    try {
      const ocrPrompt = `[AI IMAGE OCR & TYPOGRAPHY FONT EXTRACTION ENGINE]
Analyze this uploaded poster / image and extract all visible text content and font characteristics.
Format your output strictly as a JSON object with the following fields:
{
  "title": "extracted event title or poster heading",
  "memberName": "extracted person / speaker / member name",
  "designation": "extracted job title or designation",
  "company": "extracted company or organization name",
  "date": "extracted date or day",
  "venue": "extracted venue or location or time",
  "details": "extracted tagline, slogan, wish message, or full body text",
  "fontFamily": "extracted font style (e.g., Bold Sans-Serif, Serif, Calligraphy, Script)",
  "rawText": "all extracted raw text combined"
}`;

      const res = await axios.post('https://rmbbackend.rmbjaipur.com/auth/ai-generate', {
        prompt: ocrPrompt,
        image: imageUrl,
        model: 'gemini-1.5-pro',
        context: 'OCR Image Text & Typography Extraction'
      });

      const reply = res.data?.result || res.data?.reply || res.data?.response || '';
      
      let parsedJson: any = null;
      try {
        const jsonMatch = reply.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          parsedJson = JSON.parse(jsonMatch[0]);
        }
      } catch (e) {
        console.error('Failed to parse AI OCR JSON:', e);
      }

      if (parsedJson) {
        setOcrTitle(parsedJson.title || '');
        setOcrMemberName(parsedJson.memberName || '');
        setOcrDesignation(parsedJson.designation || '');
        setOcrCompany(parsedJson.company || '');
        setOcrDate(parsedJson.date || '');
        setOcrVenue(parsedJson.venue || '');
        setOcrDetails(parsedJson.details || reply);
        setOcrFontFamily(parsedJson.fontFamily || 'Bold Sans-Serif & Script');
        setOcrRawText(parsedJson.rawText || reply);
      } else {
        setOcrRawText(reply);
        setOcrDetails(reply);
        setOcrTitle('Extracted Poster Title');
        setOcrFontFamily('Bold Corporate Sans-Serif');
      }
    } catch (err) {
      console.error('Error running AI OCR on image:', err);
      setOcrRawText('Could not connect to AI OCR engine. Please check backend connection.');
    } finally {
      setIsExtractingOcr(false);
    }
  };

  const handleApplyOcrToPoster = () => {
    const newGraphic: ChatMessage['graphicData'] = {
      type: 'MEETING',
      title: ocrTitle || 'Extracted Poster',
      memberName: ocrMemberName,
      designation: ocrDesignation,
      company: ocrCompany,
      date: ocrDate,
      venue: ocrVenue,
      details: ocrDetails,
      photoUrl: ocrImageTarget,
      backgroundStyle: 'ROYAL_GOLD_BEAMS'
    };

    const assistantMsg: ChatMessage = {
      id: Date.now().toString(),
      sender: 'assistant',
      text: `🔍 **Extracted Image Text & Font OCR Applied!**\n\nI scanned the uploaded image, extracted all text and font properties, and re-created a brand-new Ultra-HD poster canvas using your edited text!`,
      graphicData: newGraphic,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, assistantMsg]);
    setShowOcrModal(false);
  };

  // Fetch Google Drive resources when picker modal opens
  useEffect(() => {
    if (showGDrivePickerModal) {
      axios.get('https://rmbbackend.rmbjaipur.com/auth/gdrive/resources')
        .then(res => {
          if (res.data?.data && Array.isArray(res.data.data)) {
            setGdriveResources(res.data.data);
          }
        })
        .catch(err => console.error('Error fetching Google Drive resources:', err));

      axios.get('https://rmbbackend.rmbjaipur.com/auth/gdrive')
        .then(res => {
          if (res.data?.link) setPrimaryDriveLink(res.data.link);
        })
        .catch(err => console.error('Error fetching primary Google Drive link:', err));
    }
  }, [showGDrivePickerModal]);

  const convertGDriveUrlToDirectUrl = (url: string) => {
    if (!url) return '';
    const fileMatch = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
    if (fileMatch && fileMatch[1]) {
      return `https://drive.google.com/uc?export=view&id=${fileMatch[1]}`;
    }
    return url;
  };

  const handleAttachGDriveResource = (resourceTitle: string, rawLink: string) => {
    if (!rawLink) return;
    const directUrl = convertGDriveUrlToDirectUrl(rawLink);
    const isImg = rawLink.match(/\.(jpeg|jpg|png|webp|gif|svg)$/i) || rawLink.includes('/d/') || rawLink.includes('image');
    
    const fileData: AttachedFile = {
      id: `gdrive_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
      url: directUrl,
      name: resourceTitle || 'Google Drive Asset',
      type: isImg ? 'image' : 'file',
      role: 'SEPARATE',
      extension: 'gdrive'
    };

    setAttachedFiles(prev => [...prev, fileData]);
    const fileRefText = `[Attached Google Drive Image/Asset: ${resourceTitle} -> ${rawLink}]`;
    setInput(prev => prev ? `${prev}\n${fileRefText}` : fileRefText);
    setShowGDrivePickerModal(false);
    setCustomGDriveUrl('');
  };

  useEffect(() => {
    try {
      localStorage.setItem('RMB_CHATGPT_ADMIN_SESSION_V3', JSON.stringify(messages));
    } catch (e) {
      console.error('Failed to save chat session:', e);
    }
  }, [messages]);

  // Custom GPT Creative Studio & RMB Event Designer Skill Master Instructions
  const MASTER_CORPORATE_POSTER_PROMPT = `[CUSTOM GPT SKILL: AADVIK CREATIVE STUDIO & CORPORATE EVENT DESIGNER]

ROLE:
You are an expert Creative Director and Graphic Designer with 15+ years of experience creating premium corporate event posters, LinkedIn creatives, sponsorship announcements, webinars, conferences, product launches, and business marketing campaigns (ISHRAE webinars, ICBL sponsorships, RMB field visits, WhatsApp campaigns, BRDs, corporate branding).

DESIGN STYLE:
- Modern Luxury Corporate Look
- Color Palette: Royal Blue (#0A2451 / #0B2C66) & Metallic Gold (#FDBC12 / #D9A11B)
- Clean white information cards, soft gradients, professional spacing, consistent visual hierarchy
- Rounded corners, soft drop shadows, minimal clutter, high-end marketing aesthetics

LAYOUT RULES:
- Top: Branded rounded white header bar for company & organizer logos
- Middle Left: Large Event Title ("RMB Jaipur Corporate Business Summit"), Script Subtitle ("You are Invited"), Subtitle & Description
- Middle Right: Professional speaker portrait inside a custom curved golden glowing frame
- Bottom: 3 Rounded White Information Cards (Date, Time, Venue) with blue icons & gold outlines
- Footer: Professional footer with CTA section, website, social handles & organizer names

BACKGROUND & TYPOGRAPHY:
- Dark navy gradients, abstract geometric textures, golden light beams, Jaipur architectural line art
- Bold sans-serif for headings (52px+), elegant script only for accent text, high contrast, print-ready 4K/8K Ultra HD (1080x1350)`;

  const handleApplyGPT4oSkillStructure = () => {
    const rawText = input.trim() || 'RMB Jaipur Corporate Business Summit & Networking Event';
    const structured = `[CUSTOM GPT SKILL: AADVIK CREATIVE STUDIO & RMB EVENT DESIGNER]

ROLE & DESIGN SYSTEM:
- Expert Creative Director (15+ yrs exp in ISHRAE webinars, ICBL sponsorships, RMB field visits, WhatsApp campaigns, BRDs)
- Style: Modern Luxury Corporate, Royal Blue (#0B2C66) & Metallic Gold (#D9A11B) theme
- Layout: Branded Header, Large Title ("${rawText}"), Script Subtitle ("You are Invited"), Portrait Cutout inside Curved Golden Frame, 3 White Info Cards (Date, Time, Venue), CTA Footer
- Quality: Adobe Illustrator style, 4K/8K Ultra HD, Print-Ready 1080x1350

${rawText}`;
    setInput(structured);
    setSelectedModel('gpt-4o');
  };
  
  // File & Media attachments state (Support multiple files & images)
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Members list for prompt injection
  const [members, setMembers] = useState<MemberOption[]>([]);
  const [selectedMemberId, setSelectedMemberId] = useState<string>('');

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom of chat
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  // Fetch members for selector
  useEffect(() => {
    axios.get('https://rmbbackend.rmbjaipur.com/auth/allusersinfo')
      .then(res => {
        const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        const activeList = list.filter((m: any) => m.delete !== 1 && (m.status === 'active' || !m.status));
        setMembers(activeList);
      })
      .catch(err => console.error('Error fetching members for AI Studio:', err));
  }, []);

  // Handle Multi-Format File & Multiple Media Upload (Images, Videos, PDFs, Word, Excel)
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploadingImage(true);
    const newAttachedFiles: AttachedFile[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append('image', file);

      const fileName = file.name;
      const ext = fileName.split('.').pop()?.toLowerCase() || '';

      let fileType: AttachedFile['type'] = 'file';
      if (['png', 'jpg', 'jpeg', 'webp', 'svg', 'gif'].includes(ext)) fileType = 'image';
      else if (['mp4', 'webm', 'mov', 'avi', 'mkv'].includes(ext)) fileType = 'video';
      else if (ext === 'pdf') fileType = 'pdf';
      else if (['doc', 'docx'].includes(ext)) fileType = 'doc';
      else if (['xls', 'xlsx', 'csv'].includes(ext)) fileType = 'excel';

      try {
        const res = await axios.post('https://rmbbackend.rmbjaipur.com/auth/upload-image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const imgPath = res.data.imageUrl || res.data.imagePath || res.data.filePath || res.data.url;
        const fullUrl = imgPath.startsWith('http') ? imgPath : `https://rmbbackend.rmbjaipur.com/${imgPath.replace(/\\/g, '/')}`;

        const fileData: AttachedFile = {
          id: `${Date.now()}_${i}_${Math.random().toString(36).substr(2, 5)}`,
          url: fullUrl,
          name: fileName,
          type: fileType,
          role: 'SEPARATE',
          extension: ext
        };
        newAttachedFiles.push(fileData);

        const fileRefText = `[Attached File ${i + 1}: ${fileName} (${fileType.toUpperCase()}) -> ${fullUrl}]`;
        setInput(prev => prev ? `${prev}\n${fileRefText}` : fileRefText);
      } catch (err) {
        // Fallback base64 / local preview
        const resultUrl = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(file);
        });

        const fileData: AttachedFile = {
          id: `${Date.now()}_${i}_${Math.random().toString(36).substr(2, 5)}`,
          url: resultUrl,
          name: fileName,
          type: fileType,
          role: 'SEPARATE',
          extension: ext
        };
        newAttachedFiles.push(fileData);

        const fileRefText = `[Attached File ${i + 1}: ${fileName} (${fileType.toUpperCase()})]`;
        setInput(prev => prev ? `${prev}\n${fileRefText}` : fileRefText);
      }
    }

    setAttachedFiles(prev => [...prev, ...newAttachedFiles]);
    setIsUploadingImage(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachedFile = (fileId: string) => {
    setAttachedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const toggleImageRole = (fileId: string, newRole: 'MEMBER' | 'SEPARATE') => {
    setAttachedFiles(prev => prev.map(f => f.id === fileId ? { ...f, role: newRole } : f));
  };

  // Inject selected member details and profile photo into prompt box
  const handleInjectMember = (memberIdStr: string) => {
    setSelectedMemberId(memberIdStr);
    if (!memberIdStr) return;

    const m = members.find(u => String(u.id) === memberIdStr);
    if (!m) return;

    let profilePic = '';
    if (m.profile) {
      profilePic = m.profile.startsWith('http') 
        ? m.profile 
        : `https://rmbbackend.rmbjaipur.com/${m.profile.replace(/\\/g, '/').replace(/^\/+/, '')}`;
      
      const memberFile: AttachedFile = {
        id: `member_profile_${m.id}`,
        url: profilePic,
        name: `${m.name}_Profile.jpg`,
        type: 'image',
        role: 'MEMBER'
      };

      setAttachedFiles(prev => {
        const filtered = prev.filter(f => !f.id.startsWith('member_profile_'));
        return [...filtered, memberFile];
      });
    }

    const injectedText = `Member: ${m.name} | Designation: ${m.designation || 'Member'} | Company: ${m.company_name || 'RMB Jaipur'} | DOB: ${m.DOB || 'Jan 1'}${profilePic ? ` | Photo: ${profilePic}` : ''}`;
    setInput(prev => prev ? `${prev}\n${injectedText}` : injectedText);
  };

  // 1080x1080 WhatsApp & Social Media High-Res Designer Poster Generator
  const handleDownloadGraphic = (graphic: ChatMessage['graphicData']) => {
    if (!graphic) return;

    const canvas = document.createElement('canvas');
    canvas.width = 1080;
    canvas.height = 1080;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 1. Draw Professional Luxury Background Gradient
    const gradient = ctx.createLinearGradient(0, 0, 1080, 1080);
    gradient.addColorStop(0, '#0F172A');
    gradient.addColorStop(0.5, '#1E293B');
    gradient.addColorStop(1, '#0B132B');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 1080, 1080);

    // 2. Draw Metallic Gold Double Border Frame
    ctx.strokeStyle = '#FDBC12';
    ctx.lineWidth = 14;
    ctx.strokeRect(24, 24, 1032, 1032);

    ctx.strokeStyle = 'rgba(253, 188, 18, 0.4)';
    ctx.lineWidth = 3;
    ctx.strokeRect(44, 44, 992, 992);

    // Corner Accents
    ctx.fillStyle = '#FDBC12';
    ctx.fillRect(24, 24, 40, 40);
    ctx.fillRect(1016, 24, 40, 40);
    ctx.fillRect(24, 1016, 40, 40);
    ctx.fillRect(1016, 1016, 40, 40);

    // 3. Top Rotary Crest Header Ribbon
    ctx.fillStyle = '#FDBC12';
    ctx.fillRect(70, 70, 940, 90);

    ctx.fillStyle = '#0A2451';
    ctx.font = 'black 34px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('ROTARY MEANS BUSINESS', 540, 115);

    ctx.font = 'bold 22px sans-serif';
    ctx.fillText('JAIPUR CHAPTER • EXECUTIVE FELLOWSHIP', 540, 145);

    // 4. Poster Category Title
    ctx.fillStyle = '#F59E0B';
    ctx.font = 'black 42px sans-serif';
    ctx.fillText(graphic.title.toUpperCase(), 540, 230);

    const triggerFileSave = (imgObj?: HTMLImageElement) => {
      let currentY = 310;

      if (imgObj) {
        // Draw Centered Circular Profile Photo Avatar with Gold Border
        const avatarCenterX = 540;
        const avatarCenterY = 410;
        const radius = 135;

        ctx.save();
        // Gold Outer Ring
        ctx.beginPath();
        ctx.arc(avatarCenterX, avatarCenterY, radius + 8, 0, Math.PI * 2);
        ctx.fillStyle = '#FDBC12';
        ctx.fill();

        // Dark Inner Ring
        ctx.beginPath();
        ctx.arc(avatarCenterX, avatarCenterY, radius + 2, 0, Math.PI * 2);
        ctx.fillStyle = '#0A2451';
        ctx.fill();

        // Photo Clip
        ctx.beginPath();
        ctx.arc(avatarCenterX, avatarCenterY, radius, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(imgObj, avatarCenterX - radius, avatarCenterY - radius, radius * 2, radius * 2);
        ctx.restore();

        currentY = 595;
      } else {
        currentY = 380;
      }

      ctx.textAlign = 'center';

      // Member Name
      if (graphic.memberName) {
        ctx.fillStyle = '#FFFFFF';
        ctx.font = 'black 48px sans-serif';
        ctx.fillText(graphic.memberName, 540, currentY);
        currentY += 50;
      }

      // Designation
      if (graphic.designation) {
        ctx.fillStyle = '#CBD5E1';
        ctx.font = 'bold 30px sans-serif';
        ctx.fillText(graphic.designation, 540, currentY);
        currentY += 45;
      }

      // Company
      if (graphic.company) {
        ctx.fillStyle = '#FBBF24';
        ctx.font = 'bold 28px sans-serif';
        ctx.fillText(`🏢 ${graphic.company}`, 540, currentY);
        currentY += 50;
      }

      // Date / Venue Box
      if (graphic.date || graphic.venue) {
        ctx.fillStyle = '#1E293B';
        ctx.fillRect(140, currentY, 800, 70);
        ctx.strokeStyle = '#FDBC12';
        ctx.lineWidth = 2;
        ctx.strokeRect(140, currentY, 800, 70);

        ctx.fillStyle = '#F59E0B';
        ctx.font = 'bold 24px sans-serif';
        const dateStr = [graphic.date, graphic.venue].filter(Boolean).join('  •  ');
        ctx.fillText(dateStr, 540, currentY + 44);
        currentY += 105;
      }

      // Customized Details / Wish Message
      if (graphic.details) {
        ctx.fillStyle = '#E2E8F0';
        ctx.font = 'italic 24px sans-serif';
        ctx.fillText(`"${graphic.details}"`, 540, currentY);
      }

      // Footer Rotary Gold Tagline
      ctx.fillStyle = '#FDBC12';
      ctx.font = 'bold 22px sans-serif';
      ctx.fillText('Service Above Self • Connecting Business Leaders Worldwide', 540, 990);

      // Trigger Browser High-Res Download
      const link = document.createElement('a');
      link.download = `rotary_${(graphic.type || 'flyer').toLowerCase()}_poster_${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    if (graphic.photoUrl) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => triggerFileSave(img);
      img.onerror = () => triggerFileSave();
      img.src = graphic.photoUrl;
    } else {
      triggerFileSave();
    }
  };

  const handleShareWhatsApp = (graphic: ChatMessage['graphicData']) => {
    if (!graphic) return;
    const text = encodeURIComponent(
      `*${graphic.title}*\n\n` +
      (graphic.memberName ? `👤 *${graphic.memberName}*\n` : '') +
      (graphic.designation ? `💼 ${graphic.designation}\n` : '') +
      (graphic.company ? `🏢 ${graphic.company}\n` : '') +
      (graphic.date ? `📅 ${graphic.date}\n` : '') +
      (graphic.venue ? `📍 ${graphic.venue}\n` : '') +
      `\n_${graphic.details || 'Rotary Means Business Fellowship'}_`
    );
    window.open(`https://api.whatsapp.com/send?text=${text}`, '_blank');
  };

  const handleSendMessage = async (textToSend?: string, graphicTypeOverride?: 'BIRTHDAY' | 'WELCOME' | 'FIELD_VISIT' | 'MEETING') => {
    const promptText = textToSend || input;
    if ((!promptText.trim() && attachedFiles.length === 0) || loading) return;

    const imageFiles = attachedFiles.filter(f => f.type === 'image');
    const imageArr = imageFiles.map(f => f.url);
    const primaryImg = imageArr[0] || undefined;

    const userMsg: ChatMessage = {
      id: Date.now().toString(),
      sender: 'user',
      text: promptText,
      image: primaryImg,
      images: imageArr,
      attachedFiles: [...attachedFiles],
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, userMsg]);
    const currentFiles = [...attachedFiles];
    if (!textToSend) setInput('');
    setAttachedFiles([]);
    setSelectedMemberId('');
    setLoading(true);

    const fullPromptWithFiles = currentFiles.length > 0
      ? `${promptText}\n[Attached Files: ${currentFiles.map(f => f.name).join(', ')}]`
      : promptText;

    try {
      const res = await axios.post('https://rmbbackend.rmbjaipur.com/auth/ai-generate', {
        prompt: fullPromptWithFiles,
        model: selectedModel,
        image: primaryImg,
        images: imageArr,
        context: 'Rotary & RMB Graphic Design & Administration'
      }).catch(() => {
        return {
          data: {
            reply: generateLocalResponse(promptText, primaryImg, graphicTypeOverride)
          }
        };
      });

      const replyText = res.data?.reply || res.data?.response || generateLocalResponse(promptText, primaryImg, graphicTypeOverride);
      const generatedGraphic = parseGraphicFromText(promptText, replyText, primaryImg, graphicTypeOverride);

      const assistantMsg: ChatMessage = {
        id: (Date.now() + 1).toString(),
        sender: 'assistant',
        text: replyText,
        graphicData: generatedGraphic,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };

      setMessages(prev => [...prev, assistantMsg]);
    } catch (err) {
      console.error('Error generating AI response:', err);
      const assistantMsg: ChatMessage = {
        id: (Date.now() + 1).toString(),
        sender: 'assistant',
        text: generateLocalResponse(promptText, primaryImg, graphicTypeOverride),
        graphicData: parseGraphicFromText(promptText, '', primaryImg, graphicTypeOverride),
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setMessages(prev => [...prev, assistantMsg]);
    } finally {
      setLoading(false);
    }
  };

  // Helper to parse graphic cards
  const parseGraphicFromText = (
    prompt: string, 
    reply: string, 
    imgUrl?: string | null, 
    typeOverride?: 'BIRTHDAY' | 'WELCOME' | 'FIELD_VISIT' | 'MEETING'
  ) => {
    const p = prompt.toLowerCase();
    
    // Extract member name if present
    const nameMatch = prompt.match(/Member:\s*([^|]+)/i) || prompt.match(/(?:for|wish|welcome)\s+([A-Za-z\s]+)/i);
    const memberName = nameMatch ? nameMatch[1].trim() : 'Rotary Member';
    
    const companyMatch = prompt.match(/Company:\s*([^|]+)/i);
    const company = companyMatch ? companyMatch[1].trim() : 'RMB Jaipur Chapter';

    const desigMatch = prompt.match(/Designation:\s*([^|]+)/i);
    const designation = desigMatch ? desigMatch[1].trim() : 'Executive Member';

    let memberPhoto = 'https://via.placeholder.com/300x300?text=Member+Photo';
    const injectedPhotoMatch = prompt.match(/Photo:\s*(http[^\s|]+)/i);
    const memberRoleFile = attachedFiles.find(f => f.role === 'MEMBER' && f.type === 'image');
    if (injectedPhotoMatch) {
      memberPhoto = injectedPhotoMatch[1];
    } else if (memberRoleFile) {
      memberPhoto = memberRoleFile.url;
    } else if (imgUrl) {
      memberPhoto = imgUrl;
    }

    if (typeOverride === 'BIRTHDAY' || p.includes('birthday') || p.includes('bday')) {
      return {
        type: 'BIRTHDAY' as const,
        title: '🎉 Happy Birthday Wish Banner',
        memberName,
        company,
        designation,
        photoUrl: memberPhoto,
        details: 'Wishing you a fantastic year filled with success, prosperity, and impactful Rotary service!'
      };
    }

    if (typeOverride === 'WELCOME' || p.includes('welcome') || p.includes('new member')) {
      return {
        type: 'WELCOME' as const,
        title: '🌟 Warm Welcome to RMB Family',
        memberName,
        company,
        designation,
        photoUrl: memberPhoto,
        details: 'We are thrilled to welcome you to the Rotary Means Business fellowship network!'
      };
    }

    if (typeOverride === 'FIELD_VISIT' || p.includes('field visit') || p.includes('1-on-1')) {
      return {
        type: 'FIELD_VISIT' as const,
        title: '📍 RMB Field Visit Announcement Graphic',
        memberName,
        company,
        date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        venue: company || 'Member Corporate Headquarters',
        photoUrl: memberPhoto,
        details: 'Fostering 1-on-1 business relationships, factory tours, and strategic synergies.'
      };
    }

    if (typeOverride === 'MEETING' || p.includes('general meeting') || p.includes('gm flyer') || p.includes('meeting flyer')) {
      return {
        type: 'MEETING' as const,
        title: '🏛️ General Meeting (GM) Official Flyer',
        date: 'Thursday | 8:00 AM Sharp',
        venue: 'Royal Ballroom, Jaipur',
        photoUrl: imgUrl || memberPhoto,
        details: 'Elevator Pitches • 1-on-1 Business Referrals • Keynote Presentation & Breakfast Fellowship'
      };
    }

    return undefined;
  };

  // Intelligent responder for Rotary Admin prompts
  const generateLocalResponse = (prompt: string, imgUrl?: string | null, typeOverride?: string): string => {
    const p = prompt.toLowerCase();
    if (p.includes('birthday') || typeOverride === 'BIRTHDAY') {
      return `🎂 **Personalized Rotary Birthday Banner Generated!**\n\nI have created a high-resolution birthday graphic banner. You can view, preview, and download the ready-to-share banner graphic right below in your chat!`;
    }
    if (p.includes('welcome') || typeOverride === 'WELCOME') {
      return `👋 **New Member Welcome Flyer Graphic Ready!**\n\nHere is your official RMB Chapter Welcome Flyer complete with Rotary branding, member designation, and photo placeholder. Download it below for social media & WhatsApp!`;
    }
    if (p.includes('field visit') || typeOverride === 'FIELD_VISIT') {
      return `📍 **RMB Field Visit Announcement Graphic Generated!**\n\nYour 1-on-1 Field Visit card has been styled with visit details, venue location, and member company profile. Download the flyer graphic directly below!`;
    }
    if (p.includes('general meeting') || p.includes('meeting flyer') || typeOverride === 'MEETING') {
      return `🏛️ **Rotary General Meeting (GM) Flyer Generated!**\n\nHere is your official Chapter Weekly General Meeting Flyer formatted with agenda, date, time, and venue details ready for publication!`;
    }
    if (p.includes('agenda')) {
      return `📅 **Official Rotary Weekly Meeting Agenda**\n\n1. **08:00 AM** - Fellowship & Breakfast Networking\n2. **08:30 AM** - Call to Order & National Anthem\n3. **08:35 AM** - President Welcome & Secretarial Announcements\n4. **08:45 AM** - Member 60-Second Business Elevator Pitches\n5. **09:15 AM** - Feature Keynote Presentation (10 Mins)\n6. **09:25 AM** - Passing Referrals & Closed Business (TYFCB)\n7. **09:40 AM** - Induction of New Members & Guest Introductions\n8. **09:50 AM** - President Closing Remarks & Adjournment.`;
    }
    return `Certainly! Regarding your request:\n\nI have processed your prompt and attached media. As your ChatGPT Rotary Studio Assistant, I can draft communications, design graphic flyers (Birthday, Welcome, Field Visit, GM Meeting), or summarize chapter data. Let me know if you need modifications!`;
  };

  const handleCopy = (id: string, text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleClearChat = () => {
    if (window.confirm('Clear current chat conversation?')) {
      setMessages([
        {
          id: '1',
          sender: 'assistant',
          text: 'Chat history cleared. How else can I assist you with graphics, flyers, or admin tasks today?',
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }
      ]);
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-4 sm:p-6">
      {/* HEADER SECTION */}
      <div className="bg-gradient-to-r from-[#0F172A] via-[#1E293B] to-[#0F172A] text-white p-6 rounded-3xl shadow-xl border border-slate-700/60 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <span className="bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 text-[10px] font-black uppercase px-3 py-1 rounded-full tracking-wider shadow flex items-center gap-1.5 w-fit mb-2">
            <FaRobot /> ChatGPT Graphic & Executive Studio
          </span>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight flex items-center gap-2">
            🎨 ChatGPT AI Graphic & Flyer Creator
          </h1>
          <p className="text-xs text-slate-300 font-semibold mt-1">
            Create **Birthday Banners**, **Welcome Flyers**, **Field Visit Graphic Cards**, & **GM Meeting Flyers** with instant image uploads & member injection!
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setShowLlmModelModal(true)}
            className="px-3.5 py-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-950 font-black rounded-xl text-xs shadow-md transition flex items-center gap-1.5 cursor-pointer"
            title="Open LLM Fine-Tuning & Learning Model Architecture"
          >
            <FaMagic size={12} /> 🧠 LLM Learning Model
          </button>

          <select
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            className="px-3 py-2 bg-slate-800 text-amber-300 font-bold text-xs rounded-xl border border-slate-600 outline-none cursor-pointer"
          >
            <option value="gpt-4o">ChatGPT-4o (Recommended)</option>
            <option value="gpt-4-turbo">GPT-4 Turbo Vision</option>
            <option value="gemini-pro">Gemini 1.5 Pro</option>
          </select>

          <button
            type="button"
            onClick={handleClearChat}
            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold rounded-xl text-xs transition border border-slate-600 flex items-center gap-1.5 cursor-pointer"
          >
            <FaTrash size={12} /> Clear
          </button>
        </div>
      </div>

      {/* QUICK GRAPHIC PRESET BUTTONS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <button
          type="button"
          onClick={() => {
            setInput(MASTER_CORPORATE_POSTER_PROMPT);
            setSelectedModel('gpt-4o');
          }}
          className="p-3.5 bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-transparent dark:bg-[#1E2B45] hover:bg-amber-100/60 dark:hover:bg-slate-800 border-2 border-amber-400/80 rounded-2xl text-left shadow-sm transition group cursor-pointer flex items-start gap-3 relative overflow-hidden"
        >
          <span className="p-2.5 rounded-xl bg-amber-500 text-slate-950 font-black text-sm flex-shrink-0 shadow">
            <FaMagic />
          </span>
          <div>
            <span className="bg-amber-500 text-slate-950 text-[9px] font-black uppercase px-1.5 py-0.5 rounded-md tracking-wider inline-block mb-1">GPT-4o Skill</span>
            <h3 className="text-xs font-black text-slate-900 dark:text-slate-100 group-hover:text-amber-600">🎨 Corporate Poster</h3>
            <p className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">Royal Blue & Gold Master Skill</p>
          </div>
        </button>

        <button
          type="button"
          onClick={() => handleSendMessage('Create a Birthday Wish Banner graphic for our Rotary member.', 'BIRTHDAY')}
          className="p-3.5 bg-white dark:bg-[#1E2B45] hover:bg-amber-50 dark:hover:bg-slate-800 border border-slate-200 dark:border-[#2A3B5C] rounded-2xl text-left shadow-sm transition group cursor-pointer flex items-start gap-3"
        >
          <span className="p-2.5 rounded-xl bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300 text-sm flex-shrink-0">
            <FaBirthdayCake />
          </span>
          <div>
            <h3 className="text-xs font-black text-slate-900 dark:text-slate-100 group-hover:text-amber-600">🎂 Birthday Banner</h3>
            <p className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">Personalized birthday wish card</p>
          </div>
        </button>

        <button
          type="button"
          onClick={() => handleSendMessage('Generate a New Member Welcome Flyer graphic with company & designation details.', 'WELCOME')}
          className="p-3.5 bg-white dark:bg-[#1E2B45] hover:bg-blue-50 dark:hover:bg-slate-800 border border-slate-200 dark:border-[#2A3B5C] rounded-2xl text-left shadow-sm transition group cursor-pointer flex items-start gap-3"
        >
          <span className="p-2.5 rounded-xl bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-300 text-sm flex-shrink-0">
            <FaUserPlus />
          </span>
          <div>
            <h3 className="text-xs font-black text-slate-900 dark:text-slate-100 group-hover:text-blue-600">👋 Welcome Flyer</h3>
            <p className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">New member induction poster</p>
          </div>
        </button>

        <button
          type="button"
          onClick={() => handleSendMessage('Design an RMB Field Visit Announcement graphic for 1-on-1 member visits.', 'FIELD_VISIT')}
          className="p-3.5 bg-white dark:bg-[#1E2B45] hover:bg-indigo-50 dark:hover:bg-slate-800 border border-slate-200 dark:border-[#2A3B5C] rounded-2xl text-left shadow-sm transition group cursor-pointer flex items-start gap-3"
        >
          <span className="p-2.5 rounded-xl bg-indigo-100 text-indigo-800 dark:bg-indigo-500/20 dark:text-indigo-300 text-sm flex-shrink-0">
            <FaMapMarkerAlt />
          </span>
          <div>
            <h3 className="text-xs font-black text-slate-900 dark:text-slate-100 group-hover:text-indigo-600">📍 Field Visit Flyer</h3>
            <p className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">1-on-1 business visit graphic</p>
          </div>
        </button>

        <button
          type="button"
          onClick={() => handleSendMessage('Create an official General Meeting (GM) Flyer with date, time, and agenda highlights.', 'MEETING')}
          className="p-3.5 bg-white dark:bg-[#1E2B45] hover:bg-emerald-50 dark:hover:bg-slate-800 border border-slate-200 dark:border-[#2A3B5C] rounded-2xl text-left shadow-sm transition group cursor-pointer flex items-start gap-3"
        >
          <span className="p-2.5 rounded-xl bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300 text-sm flex-shrink-0">
            <FaUsers />
          </span>
          <div>
            <h3 className="text-xs font-black text-slate-900 dark:text-slate-100 group-hover:text-emerald-600">🏛️ GM Meeting Flyer</h3>
            <p className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">General meeting announcement</p>
          </div>
        </button>
      </div>

      {/* CHAT MESSAGES CANVAS */}
      <div className="bg-white dark:bg-[#1E2B45] rounded-3xl shadow-sm border border-slate-200 dark:border-[#2A3B5C] overflow-hidden flex flex-col h-[600px]">
        {/* MESSAGES LIST */}
        <div className="flex-1 p-4 sm:p-6 overflow-y-auto space-y-4">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex gap-3 max-w-3xl ${
                msg.sender === 'user' ? 'ml-auto flex-row-reverse' : ''
              }`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-black flex-shrink-0 ${
                  msg.sender === 'user'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gradient-to-tr from-amber-500 to-amber-600 text-slate-950 shadow'
                }`}
              >
                {msg.sender === 'user' ? <FaUser size={12} /> : <FaRobot size={14} />}
              </div>

              <div className="space-y-2 max-w-full">
                <div
                  className={`p-4 rounded-2xl text-xs leading-relaxed space-y-2 relative group shadow-sm ${
                    msg.sender === 'user'
                      ? 'bg-blue-600 text-white rounded-tr-none font-medium'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-tl-none border border-slate-200 dark:border-slate-700'
                  }`}
                >
                  {/* User Attached Multi-Image & File Gallery */}
                  {msg.images && msg.images.length > 0 ? (
                    <div className="mb-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {msg.images.map((imgUrl, idx) => (
                        <div key={idx} className="relative group overflow-hidden rounded-xl border border-white/20 shadow-md bg-black/20">
                          <img
                            src={imgUrl}
                            alt={`Attachment ${idx + 1}`}
                            className="w-full h-24 object-cover hover:scale-105 transition duration-200"
                          />
                          <button
                            type="button"
                            onClick={() => handleExtractTextFromImage(imgUrl)}
                            className="absolute top-1 right-1 bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-[9px] px-2 py-0.5 rounded-md shadow cursor-pointer flex items-center gap-1 opacity-90 hover:opacity-100 transition"
                            title="Extract Text & Font from Image with AI OCR"
                          >
                            <FaSearch size={8} /> OCR Text
                          </button>
                          <span className="absolute bottom-1 right-1 bg-black/60 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">
                            #{idx + 1}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : msg.image && (
                    <div className="mb-2 relative group max-w-fit">
                      <img
                        src={msg.image}
                        alt="User Attachment"
                        className="max-h-48 rounded-xl border border-white/20 shadow-md object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => handleExtractTextFromImage(msg.image!)}
                        className="absolute top-2 right-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-xs px-2.5 py-1 rounded-lg shadow-md cursor-pointer flex items-center gap-1 opacity-90 hover:opacity-100 transition"
                        title="Extract Text & Font from Image with AI OCR"
                      >
                        <FaSearch size={10} /> Extract Text & Font (OCR)
                      </button>
                    </div>
                  )}

                  <div className="whitespace-pre-wrap">{msg.text}</div>

                  {msg.sender === 'assistant' && (
                    <div className="pt-2 flex items-center justify-between text-[10px] text-slate-400 border-t border-slate-200 dark:border-slate-700/60 mt-2">
                      <span>{msg.timestamp}</span>
                      <button
                        type="button"
                        onClick={() => handleCopy(msg.id, msg.text)}
                        className="hover:text-blue-600 dark:hover:text-amber-400 font-bold flex items-center gap-1 cursor-pointer"
                      >
                        {copiedId === msg.id ? <><FaCheck className="text-emerald-500" /> Copied</> : <><FaCopy /> Copy</>}
                      </button>
                    </div>
                  )}
                </div>

                {/* HIGH-RES GRAPHIC POSTER CANVAS RENDERER */}
                {msg.graphicData && (
                  <GraphicPosterCanvas 
                    graphic={msg.graphicData} 
                    onEditGraphic={(g) => handleOpenPosterCustomizer(msg.id, g)}
                  />
                )}
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex gap-3 max-w-md">
              <div className="w-8 h-8 rounded-full bg-amber-500 text-slate-950 flex items-center justify-center text-xs font-black animate-spin">
                <FaMagic size={14} />
              </div>
              <div className="bg-slate-100 dark:bg-slate-800 p-4 rounded-2xl text-xs text-slate-500 dark:text-slate-400 font-bold animate-pulse">
                ChatGPT is creating graphic flyer & layout...
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* PROMPT INPUT CANVAS WITH ATTACHMENTS & MEMBER INJECTOR */}
        <div className="p-4 bg-slate-50 dark:bg-[#131E32] border-t border-slate-200 dark:border-[#2A3B5C] space-y-3">
          {/* MULTI-FILE & MULTI-IMAGE ATTACHMENTS PREVIEW GALLERY BAR */}
          {attachedFiles.length > 0 && (
            <div className="space-y-2 bg-amber-50/80 dark:bg-slate-800/80 p-3 rounded-2xl border border-amber-300 dark:border-amber-500/40 shadow-sm">
              <div className="flex items-center justify-between text-[11px] font-extrabold text-slate-700 dark:text-slate-200 border-b border-amber-200 dark:border-slate-700 pb-1.5">
                <span>📎 {attachedFiles.length} File(s) Attached to Prompt:</span>
                <button
                  type="button"
                  onClick={() => setAttachedFiles([])}
                  className="text-red-500 hover:underline text-[10px] cursor-pointer"
                >
                  Clear All
                </button>
              </div>

              <div className="flex flex-wrap gap-2.5 max-h-48 overflow-y-auto p-1">
                {attachedFiles.map((file) => (
                  <div
                    key={file.id}
                    className="flex items-center gap-2 bg-white dark:bg-slate-900 p-2 rounded-xl border border-slate-200 dark:border-slate-700 shadow-xs"
                  >
                    {file.type === 'image' && (
                      <img src={file.url} alt={file.name} className="w-10 h-10 rounded-lg object-cover border border-amber-400" />
                    )}
                    {file.type === 'video' && (
                      <span className="p-2 rounded-lg bg-purple-500/20 text-purple-400 text-sm">
                        <FaVideo />
                      </span>
                    )}
                    {file.type === 'pdf' && (
                      <span className="p-2 rounded-lg bg-red-500/20 text-red-400 text-sm">
                        <FaFilePdf />
                      </span>
                    )}
                    {file.type === 'doc' && (
                      <span className="p-2 rounded-lg bg-blue-500/20 text-blue-400 text-sm">
                        <FaFileWord />
                      </span>
                    )}
                    {file.type === 'excel' && (
                      <span className="p-2 rounded-lg bg-emerald-500/20 text-emerald-400 text-sm">
                        <FaFileExcel />
                      </span>
                    )}
                    {file.type === 'file' && (
                      <span className="p-2 rounded-lg bg-slate-500/20 text-slate-400 text-sm">
                        <FaFileAlt />
                      </span>
                    )}

                    <div className="max-w-[130px] truncate">
                      <div className="text-[11px] font-bold text-slate-800 dark:text-slate-100 truncate">{file.name}</div>
                      <div className="text-[9px] font-bold text-amber-600 dark:text-amber-400 uppercase">{file.type}</div>
                    </div>

                    {file.type === 'image' && (
                      <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-md text-[9px] font-bold">
                        <button
                          type="button"
                          onClick={() => toggleImageRole(file.id, 'MEMBER')}
                          className={`px-1.5 py-0.5 rounded cursor-pointer transition ${
                            file.role === 'MEMBER'
                              ? 'bg-amber-500 text-slate-950 font-black'
                              : 'text-slate-500 dark:text-slate-400'
                          }`}
                          title="Use as Member Portrait Avatar"
                        >
                          👤 Avatar
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleImageRole(file.id, 'SEPARATE')}
                          className={`px-1.5 py-0.5 rounded cursor-pointer transition ${
                            file.role === 'SEPARATE' || !file.role
                              ? 'bg-amber-500 text-slate-950 font-black'
                              : 'text-slate-500 dark:text-slate-400'
                          }`}
                          title="Use as Separate Graphic / Poster Image"
                        >
                          🖼️ Poster
                        </button>
                        <button
                          type="button"
                          onClick={() => handleExtractTextFromImage(file.url)}
                          className="px-1.5 py-0.5 rounded cursor-pointer transition bg-blue-600 hover:bg-blue-700 text-white font-black flex items-center gap-1"
                          title="Extract text & font styles from this image with AI OCR"
                        >
                          <FaSearch size={8} /> OCR Text
                        </button>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() => removeAttachedFile(file.id)}
                      className="text-slate-400 hover:text-red-500 p-1 cursor-pointer ml-1"
                      title="Remove file"
                    >
                      <FaTimes />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CONTROLS BAR: MEMBER SELECTOR & MULTI-FORMAT FILE UPLOADER */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 flex-1 min-w-[240px]">
              <span className="text-xs font-bold text-slate-500 dark:text-slate-400 flex items-center gap-1">
                👤 Inject Member:
              </span>
              <select
                value={selectedMemberId}
                onChange={(e) => handleInjectMember(e.target.value)}
                className="flex-1 p-2 bg-white dark:bg-[#1E2B45] text-slate-800 dark:text-slate-100 border border-slate-300 dark:border-[#2A3B5C] rounded-xl text-xs font-bold outline-none cursor-pointer"
              >
                <option value="">-- Select Member to Insert Details & Photo --</option>
                {members.map(m => (
                  <option key={m.id} value={m.id}>
                    {m.name} ({m.company_name || 'RMB Member'})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleApplyGPT4oSkillStructure}
                className="px-3 py-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-950 font-black rounded-xl text-xs transition shadow-sm flex items-center gap-1.5 cursor-pointer"
                title="Convert current text into GPT-4o 8-Step Corporate Design Structure"
              >
                <FaMagic size={11} />
                <span>✨ Format with GPT-4o Skill</span>
              </button>

              <button
                type="button"
                onClick={() => setShowSkillGuideModal(true)}
                className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-amber-300 font-black rounded-xl text-xs transition border border-slate-700 flex items-center gap-1.5 cursor-pointer shadow-sm"
                title="Open GPT-4o Corporate Event Poster Skill Guide"
              >
                <FaLightbulb size={11} />
                <span>📖 Skill Guide</span>
              </button>

              <button
                type="button"
                onClick={() => setShowGDrivePickerModal(true)}
                className="px-3 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold rounded-xl text-xs transition border border-blue-500/40 flex items-center gap-1.5 cursor-pointer shadow-sm"
                title="Select and attach an image or resource from connected Google Drive"
              >
                <FaGoogleDrive className="text-amber-400" />
                <span>📂 Attach from Google Drive</span>
              </button>

              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                multiple
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingImage}
                className="px-3 py-2 bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-100 font-bold rounded-xl text-xs transition border border-slate-300 dark:border-slate-700 flex items-center gap-1.5 cursor-pointer shadow-sm"
              >
                <FaPaperclip className="text-amber-500" />
                <span>{isUploadingImage ? 'Uploading...' : '📎 Attach File / Media'}</span>
              </button>
            </div>
          </div>

          {/* MAIN PROMPT FORM */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSendMessage();
            }}
            className="flex items-end gap-2"
          >
            <textarea
              rows={2}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              placeholder="Type prompt or click 'Format with GPT-4o Skill'... (Shift+Enter for new line, Enter to submit)"
              className="flex-1 p-3 bg-white dark:bg-[#1E2B45] border border-slate-300 dark:border-[#2A3B5C] rounded-2xl text-xs font-bold text-slate-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-amber-500 shadow-sm resize-none min-h-[52px] max-h-36"
            />
            <button
              type="submit"
              disabled={loading || (!input.trim() && attachedFiles.length === 0)}
              className="px-5 py-3.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-950 font-black rounded-2xl text-xs shadow-md transition disabled:opacity-50 flex items-center gap-1.5 cursor-pointer h-[52px]"
            >
              <span>Submit</span> <FaPaperPlane size={11} />
            </button>
          </form>
        </div>
      </div>

      {/* GPT-4O CORPORATE EVENT POSTER SKILL GUIDE MODAL */}
      {showSkillGuideModal && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 text-white w-full max-w-3xl max-h-[85vh] overflow-y-auto p-6 rounded-3xl shadow-2xl space-y-6 border border-amber-500/40">
            {/* MODAL HEADER */}
            <div className="flex items-center justify-between border-b border-slate-800 pb-4">
              <div className="flex items-center gap-2">
                <span className="p-2 rounded-xl bg-amber-500 text-slate-950 text-base font-black">🎨</span>
                <div>
                  <h2 className="text-lg font-black text-amber-300">GPT-4o Corporate Event Poster Creation Skill</h2>
                  <p className="text-xs text-slate-400 font-medium">Professional 8-Step Structured Prompt Engineering & Multi-Stage Workflow</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowSkillGuideModal(false)}
                className="text-slate-400 hover:text-white font-black text-xl p-1.5"
              >
                ✕
              </button>
            </div>

            {/* 8-STEP DESIGN SYSTEM */}
            <div className="space-y-3 bg-slate-950/60 p-4 rounded-2xl border border-slate-800">
              <h3 className="text-xs font-black text-amber-400 uppercase tracking-wider flex items-center gap-1.5">
                <FaMagic /> 8-Step Structured Prompt System
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                <div className="bg-slate-900 p-3 rounded-xl border border-slate-800">
                  <strong className="text-amber-300">1. Design Style:</strong> Modern Luxury, Royal Blue & Gold, White Info Cards, Adobe Illustrator 4K Quality.
                </div>
                <div className="bg-slate-900 p-3 rounded-xl border border-slate-800">
                  <strong className="text-amber-300">2. Layout:</strong> Top white header, Middle left title/description, Middle right portrait frame, Bottom 3 cards, Footer.
                </div>
                <div className="bg-slate-900 p-3 rounded-xl border border-slate-800">
                  <strong className="text-amber-300">3. Color Palette:</strong> Royal Blue (#0B2C66), Metallic Gold (#D9A11B), Deep Navy Gradient.
                </div>
                <div className="bg-slate-900 p-3 rounded-xl border border-slate-800">
                  <strong className="text-amber-300">4. Typography:</strong> Huge bold sans-serif title, elegant script subtitle, extra bold info.
                </div>
                <div className="bg-slate-900 p-3 rounded-xl border border-slate-800">
                  <strong className="text-amber-300">5. Background:</strong> Luxury navy gradient, golden light beams, Jaipur architectural line art.
                </div>
                <div className="bg-slate-900 p-3 rounded-xl border border-slate-800">
                  <strong className="text-amber-300">6. Portrait:</strong> Business attire, soft studio lighting inside a curved golden ring border.
                </div>
                <div className="bg-slate-900 p-3 rounded-xl border border-slate-800">
                  <strong className="text-amber-300">7. Info Cards:</strong> Rounded white cards with blue icons and gold outlines.
                </div>
                <div className="bg-slate-900 p-3 rounded-xl border border-slate-800">
                  <strong className="text-amber-300">8. Footer:</strong> Blue footer with gold dividers, website, social handles, organizer details.
                </div>
              </div>
            </div>

            {/* MASTER PROMPT BOX */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-black text-amber-300 uppercase tracking-wider">🌟 Ready-to-Use Master Prompt (1080×1350)</span>
                <button
                  type="button"
                  onClick={() => {
                    setInput(MASTER_CORPORATE_POSTER_PROMPT);
                    setSelectedModel('gpt-4o');
                    setShowSkillGuideModal(false);
                  }}
                  className="px-3 py-1 bg-amber-500 text-slate-950 font-black rounded-lg text-xs hover:bg-amber-400 transition"
                >
                  Load Master Prompt into Chat
                </button>
              </div>
              <textarea
                readOnly
                rows={5}
                value={MASTER_CORPORATE_POSTER_PROMPT}
                className="w-full p-3 bg-slate-950 text-slate-200 text-xs rounded-2xl border border-slate-800 outline-none font-mono resize-none"
              />
            </div>

            {/* STAGED WORKFLOW RECOMMENDATION */}
            <div className="bg-blue-950/40 p-4 rounded-2xl border border-blue-800/50 space-y-2 text-xs text-blue-200">
              <h4 className="font-black text-amber-300 text-xs flex items-center gap-1.5">
                ⚡ Recommended Professional Staged Workflow
              </h4>
              <ul className="list-disc list-inside space-y-1 text-slate-300 font-medium">
                <li><strong>Step 1:</strong> Generate the luxury navy & gold background asset with GPT-4o.</li>
                <li><strong>Step 2:</strong> Generate or isolate the member portrait inside the curved golden frame separately.</li>
                <li><strong>Step 3:</strong> Compose final poster text, logos, and info cards in Canva, Adobe Express, or Photoshop for 100% crisp typography alignment!</li>
              </ul>
            </div>

            {/* FOOTER ACTIONS */}
            <div className="flex items-center justify-end gap-3 border-t border-slate-800 pt-4">
              <button
                type="button"
                onClick={() => setShowSkillGuideModal(false)}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold rounded-xl text-xs transition"
              >
                Close Guide
              </button>
              <button
                type="button"
                onClick={() => {
                  setInput(MASTER_CORPORATE_POSTER_PROMPT);
                  setSelectedModel('gpt-4o');
                  setShowSkillGuideModal(false);
                }}
                className="px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-black rounded-xl text-xs shadow hover:from-amber-600 hover:to-amber-700 transition"
              >
                Load Master Prompt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* LLM FINE-TUNING & LEARNING MODEL ARCHITECTURE MODAL */}
      {showLlmModelModal && (
        <div className="fixed inset-0 bg-slate-950/85 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 text-white w-full max-w-3xl max-h-[85vh] overflow-y-auto p-6 rounded-3xl shadow-2xl space-y-6 border border-amber-500/50">
            <div className="flex items-center justify-between border-b border-slate-800 pb-4">
              <div className="flex items-center gap-2">
                <span className="p-2.5 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 text-slate-950 text-xl font-black">🧠</span>
                <div>
                  <h2 className="text-lg font-black text-amber-300">LLM Graphic Learning & Fine-Tuning Engine</h2>
                  <p className="text-xs text-slate-400 font-medium">ChatGPT-4o Corporate Image Model Architecture & Design Tokens</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowLlmModelModal(false)}
                className="text-slate-400 hover:text-white font-black text-xl p-1.5"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4 text-xs text-slate-300">
              <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-2">
                <h3 className="font-black text-amber-400 text-sm flex items-center gap-1.5">
                  ⚡ Model Learning Architecture
                </h3>
                <p>
                  This LLM Learning Model trains GPT-4o on exact Rotary International & RMB brand guidelines. Rather than generating low-fidelity unstructured text, the model processes prompts through an 8-layer design token system:
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-slate-950/80 p-3.5 rounded-xl border border-amber-500/30">
                  <strong className="text-amber-300 block mb-1">1. Color Palette Tokens:</strong>
                  Royal Blue (#0A2451 / #0B2C66), Metallic Gold (#FDBC12 / #D9A11B), Slate Navy (#131E32), Crisp White (#FFFFFF).
                </div>

                <div className="bg-slate-950/80 p-3.5 rounded-xl border border-amber-500/30">
                  <strong className="text-amber-300 block mb-1">2. Typography Rules:</strong>
                  Title: 52px+ Sans-Serif Bold. Subtitle: Elegant Script. Details: 24px Italic. Info Cards: 30px Extra-Bold.
                </div>

                <div className="bg-slate-950/80 p-3.5 rounded-xl border border-amber-500/30">
                  <strong className="text-amber-300 block mb-1">3. Avatar & Framing Engine:</strong>
                  Automatic double-ring gold glow border. Auto-resolves missing photos to Metallic Gold Initials Avatar Badges.
                </div>

                <div className="bg-slate-950/80 p-3.5 rounded-xl border border-amber-500/30">
                  <strong className="text-amber-300 block mb-1">4. Ultra-HD Canvas Rendering:</strong>
                  Renders directly on HTML5 Canvas (1080x1080 / 1080x1350) for 100% crisp PNG export with zero quality loss.
                </div>
              </div>

              <div className="bg-amber-950/30 p-4 rounded-2xl border border-amber-500/40 space-y-2">
                <h4 className="font-black text-amber-300">💡 Fine-Tuned Prompt Generation Instructions:</h4>
                <p className="text-slate-300 font-mono text-[11px] leading-relaxed">
                  "Create a corporate poster for [Member Name] | Company: [Company] | Designation: [Title] | Event: [Event Name] | Date: [Date] | Venue: [Venue]"
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end border-t border-slate-800 pt-4">
              <button
                type="button"
                onClick={() => setShowLlmModelModal(false)}
                className="px-5 py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-black rounded-xl text-xs shadow hover:from-amber-600 hover:to-amber-700 transition"
              >
                Done / Close Model
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI OCR IMAGE TEXT & FONT EXTRACTOR MODAL */}
      {showOcrModal && (
        <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 text-white w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6 rounded-3xl shadow-2xl space-y-6 border border-blue-500/50">
            {/* MODAL HEADER */}
            <div className="flex items-center justify-between border-b border-slate-800 pb-4">
              <div className="flex items-center gap-3">
                <span className="p-3 rounded-2xl bg-blue-600 text-amber-400 text-xl font-black shadow-lg">
                  <FaSearch />
                </span>
                <div>
                  <h2 className="text-lg font-black text-amber-300 flex items-center gap-2">
                    AI Image Text & Font OCR Extractor
                  </h2>
                  <p className="text-xs text-slate-400 font-medium">Extract typography, titles, member names, dates & font styles from uploaded images to make them 100% editable</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowOcrModal(false)}
                className="text-slate-400 hover:text-white font-black text-xl p-1.5 cursor-pointer"
              >
                ✕
              </button>
            </div>

            {isExtractingOcr ? (
              <div className="py-16 text-center space-y-4">
                <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                <div className="space-y-1">
                  <h3 className="text-base font-black text-amber-300">Scanning Image with AI Vision & OCR Engine...</h3>
                  <p className="text-xs text-slate-400">Analyzing text layout, typography fonts, headings, dates, and member details...</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                {/* LEFT COLUMN: IMAGE PREVIEW */}
                <div className="md:col-span-5 space-y-3 bg-slate-950 p-4 rounded-2xl border border-slate-800 flex flex-col justify-between">
                  <div>
                    <span className="text-[10px] font-black text-blue-400 uppercase tracking-wider block mb-2">
                      🖼️ Uploaded Target Image
                    </span>
                    {ocrImageTarget ? (
                      <img
                        src={ocrImageTarget}
                        alt="OCR Target"
                        className="w-full max-h-72 object-contain rounded-xl border border-slate-800 bg-black/40 shadow"
                      />
                    ) : (
                      <div className="h-48 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-500 text-xs">
                        No image preview available
                      </div>
                    )}
                  </div>

                  <div className="bg-blue-950/40 p-3 rounded-xl border border-blue-500/30 space-y-1">
                    <span className="text-[10px] font-black text-amber-400 uppercase flex items-center gap-1">
                      <FaFont /> Extracted Font & Typography Style:
                    </span>
                    <p className="text-xs font-bold text-slate-200">{ocrFontFamily}</p>
                  </div>
                </div>

                {/* RIGHT COLUMN: EDITABLE EXTRACTED FIELDS */}
                <div className="md:col-span-7 space-y-4 text-xs">
                  <div className="bg-slate-950 p-3 rounded-xl border border-slate-800 flex items-center justify-between">
                    <span className="font-extrabold text-amber-400 text-xs">✏️ Edit Extracted Text & Fields Below:</span>
                    <span className="text-[10px] font-bold text-slate-400 bg-slate-900 px-2 py-0.5 rounded border border-slate-800">
                      OCR Extracted Ready
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1 sm:col-span-2">
                      <label className="font-bold text-amber-400 text-[11px]">Extracted Title / Heading:</label>
                      <input
                        type="text"
                        value={ocrTitle}
                        onChange={(e) => setOcrTitle(e.target.value)}
                        placeholder="Extracted Event Title"
                        className="w-full p-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-amber-500"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="font-bold text-slate-300 text-[11px]">Extracted Member / Speaker:</label>
                      <input
                        type="text"
                        value={ocrMemberName}
                        onChange={(e) => setOcrMemberName(e.target.value)}
                        placeholder="Extracted Name"
                        className="w-full p-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-amber-500"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="font-bold text-slate-300 text-[11px]">Extracted Designation:</label>
                      <input
                        type="text"
                        value={ocrDesignation}
                        onChange={(e) => setOcrDesignation(e.target.value)}
                        placeholder="Extracted Job Title"
                        className="w-full p-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-amber-500"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="font-bold text-slate-300 text-[11px]">Extracted Company:</label>
                      <input
                        type="text"
                        value={ocrCompany}
                        onChange={(e) => setOcrCompany(e.target.value)}
                        placeholder="Extracted Company"
                        className="w-full p-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-amber-500"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="font-bold text-amber-400 text-[11px]">Extracted Date & Time:</label>
                      <input
                        type="text"
                        value={ocrDate}
                        onChange={(e) => setOcrDate(e.target.value)}
                        placeholder="Extracted Date"
                        className="w-full p-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-amber-500"
                      />
                    </div>

                    <div className="space-y-1 sm:col-span-2">
                      <label className="font-bold text-amber-400 text-[11px]">Extracted Venue / Location:</label>
                      <input
                        type="text"
                        value={ocrVenue}
                        onChange={(e) => setOcrVenue(e.target.value)}
                        placeholder="Extracted Venue"
                        className="w-full p-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-amber-500"
                      />
                    </div>

                    <div className="space-y-1 sm:col-span-2">
                      <label className="font-bold text-slate-300 text-[11px]">Extracted Body Text / Tagline / Wish:</label>
                      <textarea
                        rows={3}
                        value={ocrDetails}
                        onChange={(e) => setOcrDetails(e.target.value)}
                        placeholder="Extracted description or tagline..."
                        className="w-full p-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-amber-500"
                      />
                    </div>

                    <div className="space-y-1 sm:col-span-2">
                      <label className="font-bold text-slate-400 text-[10px]">Font Style Descriptor:</label>
                      <input
                        type="text"
                        value={ocrFontFamily}
                        onChange={(e) => setOcrFontFamily(e.target.value)}
                        placeholder="e.g. Modern Bold Sans-Serif & Gold Script Accents"
                        className="w-full p-2 bg-slate-950 border border-slate-800 rounded-xl text-[11px] font-bold text-amber-300 outline-none"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* MODAL FOOTER */}
            <div className="flex items-center justify-between border-t border-slate-800 pt-4">
              <button
                type="button"
                onClick={() => handleExtractTextFromImage(ocrImageTarget)}
                disabled={isExtractingOcr}
                className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold rounded-xl text-xs transition cursor-pointer flex items-center gap-1.5"
              >
                🔄 Re-Scan Image OCR
              </button>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowOcrModal(false)}
                  className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold rounded-xl text-xs transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={isExtractingOcr}
                  onClick={handleApplyOcrToPoster}
                  className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-black rounded-xl text-xs shadow-lg transition cursor-pointer flex items-center gap-1.5"
                >
                  <span>✨ Apply Extracted Text to Poster Canvas</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* POSTER FIELDS & BACKGROUND CUSTOMIZER MODAL */}
      {showPosterCustomizerModal && (
        <div className="fixed inset-0 bg-slate-950/85 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 text-white w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6 rounded-3xl shadow-2xl space-y-6 border border-amber-500/50">
            {/* MODAL HEADER */}
            <div className="flex items-center justify-between border-b border-slate-800 pb-4">
              <div className="flex items-center gap-2.5">
                <span className="p-2.5 rounded-2xl bg-amber-500 text-slate-950 text-xl font-black">⚙️</span>
                <div>
                  <h2 className="text-lg font-black text-amber-300">Customize Poster Fields & Background Image</h2>
                  <p className="text-xs text-slate-400 font-medium">Fine-tune poster fields, background themes, or attach custom background graphics</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowPosterCustomizerModal(false)}
                className="text-slate-400 hover:text-white font-black text-xl p-1.5 cursor-pointer"
              >
                ✕
              </button>
            </div>

            {/* FORM FIELDS GRID */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
              <div className="space-y-1.5 sm:col-span-2">
                <label className="font-bold text-amber-400">Poster Category / Title</label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  placeholder="e.g. 🎉 Happy Birthday Wish Banner, 🏛️ General Meeting (GM)"
                  className="w-full p-3 bg-slate-950 border border-slate-800 rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>

              <div className="space-y-1.5">
                <label className="font-bold text-slate-300">Member Name / Speaker</label>
                <input
                  type="text"
                  value={editMemberName}
                  onChange={(e) => setEditMemberName(e.target.value)}
                  placeholder="e.g. Rtn. Rajesh Sharma"
                  className="w-full p-3 bg-slate-950 border border-slate-800 rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>

              <div className="space-y-1.5">
                <label className="font-bold text-slate-300">Designation / Title</label>
                <input
                  type="text"
                  value={editDesignation}
                  onChange={(e) => setEditDesignation(e.target.value)}
                  placeholder="e.g. Managing Director / Keynote Speaker"
                  className="w-full p-3 bg-slate-950 border border-slate-800 rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>

              <div className="space-y-1.5">
                <label className="font-bold text-slate-300">Company Name</label>
                <input
                  type="text"
                  value={editCompany}
                  onChange={(e) => setEditCompany(e.target.value)}
                  placeholder="e.g. Prime Tech Solutions Pvt Ltd"
                  className="w-full p-3 bg-slate-950 border border-slate-800 rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>

              <div className="space-y-1.5">
                <label className="font-bold text-amber-400">Date & Day</label>
                <input
                  type="text"
                  value={editDate}
                  onChange={(e) => setEditDate(e.target.value)}
                  placeholder="e.g. Thursday | July 30, 2026"
                  className="w-full p-3 bg-slate-950 border border-slate-800 rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <label className="font-bold text-amber-400">Venue & Time</label>
                <input
                  type="text"
                  value={editVenue}
                  onChange={(e) => setEditVenue(e.target.value)}
                  placeholder="e.g. 📍 Royal Ballroom, Hotel Marriott, Jaipur • ⏰ 08:00 AM Sharp"
                  className="w-full p-3 bg-slate-950 border border-slate-800 rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <label className="font-bold text-slate-300">Wish / Tagline / Event Description</label>
                <textarea
                  rows={2}
                  value={editDetails}
                  onChange={(e) => setEditDetails(e.target.value)}
                  placeholder="Enter greeting message, agenda highlights, or slogan..."
                  className="w-full p-3 bg-slate-950 border border-slate-800 rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>

              {/* PHOTO URL & GOOGLE DRIVE INTEGRATION */}
              <div className="space-y-1.5 sm:col-span-2">
                <label className="font-bold text-amber-400 flex items-center justify-between">
                  <span>Member Photo URL</span>
                  <button
                    type="button"
                    onClick={() => {
                      setShowPosterCustomizerModal(false);
                      setShowGDrivePickerModal(true);
                    }}
                    className="text-[10px] text-blue-400 hover:underline flex items-center gap-1 cursor-pointer"
                  >
                    <FaGoogleDrive /> Pick from Google Drive
                  </button>
                </label>
                <input
                  type="text"
                  value={editPhotoUrl}
                  onChange={(e) => setEditPhotoUrl(e.target.value)}
                  placeholder="https://rmbbackend.rmbjaipur.com/uploads/... or Google Drive Image URL"
                  className="w-full p-3 bg-slate-950 border border-slate-800 rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>

              {/* BACKGROUND THEME PRESET */}
              <div className="space-y-1.5 sm:col-span-2 bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-3">
                <h4 className="font-black text-amber-400 text-xs">🖼️ Background Image & Luxury Theme Creator</h4>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="font-bold text-slate-300 block mb-1 text-[11px]">Luxury Background Style:</label>
                    <select
                      value={editBackgroundStyle}
                      onChange={(e) => setEditBackgroundStyle(e.target.value)}
                      className="w-full p-2.5 bg-slate-900 border border-slate-700 rounded-xl text-xs font-bold text-amber-300 outline-none cursor-pointer"
                    >
                      <option value="ROYAL_GOLD_BEAMS">Royal Blue & Gold Beams (#0B2C66)</option>
                      <option value="DEEP_NAVY_LUXURY">Deep Midnight Navy (#0A192F)</option>
                      <option value="EMERALD_ELEGANCE">Emerald Green & Gold (#064E3B)</option>
                      <option value="CUSTOM_IMAGE">Custom Background Image URL</option>
                    </select>
                  </div>

                  <div>
                    <label className="font-bold text-slate-300 block mb-1 text-[11px]">Custom Background Image URL:</label>
                    <input
                      type="text"
                      value={editBgImageUrl}
                      onChange={(e) => {
                        setEditBgImageUrl(e.target.value);
                        if (e.target.value) setEditBackgroundStyle('CUSTOM_IMAGE');
                      }}
                      placeholder="Paste background texture URL or Google Drive link"
                      className="w-full p-2.5 bg-slate-900 border border-slate-700 rounded-xl text-xs font-bold text-white outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* MODAL FOOTER */}
            <div className="flex items-center justify-end gap-3 border-t border-slate-800 pt-4">
              <button
                type="button"
                onClick={() => setShowPosterCustomizerModal(false)}
                className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold rounded-xl text-xs transition cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSavePosterCustomizer}
                className="px-5 py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-950 font-black rounded-xl text-xs shadow transition cursor-pointer"
              >
                🎨 Save & Re-Render Ultra-HD Poster
              </button>
            </div>
          </div>
        </div>
      )}

      {/* GOOGLE DRIVE IMAGE & RESOURCE PICKER MODAL */}
      {showGDrivePickerModal && (
        <div className="fixed inset-0 bg-slate-950/85 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 text-white w-full max-w-3xl max-h-[85vh] overflow-y-auto p-6 rounded-3xl shadow-2xl space-y-6 border border-blue-500/50">
            {/* MODAL HEADER */}
            <div className="flex items-center justify-between border-b border-slate-800 pb-4">
              <div className="flex items-center gap-2">
                <span className="p-2.5 rounded-2xl bg-blue-600 text-amber-400 text-xl font-black">
                  <FaGoogleDrive />
                </span>
                <div>
                  <h2 className="text-lg font-black text-white">Attach Image / Asset from Google Drive</h2>
                  <p className="text-xs text-slate-400 font-medium">Select a saved Google Drive folder/image link or paste a shareable link</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowGDrivePickerModal(false)}
                className="text-slate-400 hover:text-white font-black text-xl p-1.5"
              >
                ✕
              </button>
            </div>

            {/* DIRECT PASTE LINK BAR */}
            <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-3">
              <span className="text-xs font-black text-amber-400 uppercase tracking-wider block">
                🔗 Option 1: Direct Google Drive Image / File Link
              </span>
              <div className="flex items-center gap-2">
                <input
                  type="url"
                  value={customGDriveUrl}
                  onChange={(e) => setCustomGDriveUrl(e.target.value)}
                  placeholder="Paste Google Drive URL (e.g. https://drive.google.com/file/d/1-xyz.../view)"
                  className="flex-1 p-3 bg-slate-900 border border-slate-700 rounded-xl text-xs font-bold text-slate-100 outline-none focus:ring-2 focus:ring-amber-500"
                />
                <button
                  type="button"
                  disabled={!customGDriveUrl.trim()}
                  onClick={() => handleAttachGDriveResource('Google Drive Image Link', customGDriveUrl)}
                  className="px-4 py-3 bg-amber-500 hover:bg-amber-400 text-slate-950 font-black rounded-xl text-xs shadow transition disabled:opacity-50 flex items-center gap-1.5 cursor-pointer flex-shrink-0"
                >
                  Attach Link
                </button>
              </div>
            </div>

            {/* SAVED DRIVE RESOURCES LIST */}
            <div className="space-y-3">
              <span className="text-xs font-black text-slate-300 uppercase tracking-wider block">
                📂 Option 2: Saved Chapter Google Drive Repositories
              </span>

              {primaryDriveLink && (
                <div className="bg-blue-950/40 p-4 rounded-2xl border border-blue-600/50 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                  <div>
                    <span className="text-[10px] font-black text-amber-400 uppercase px-2 py-0.5 rounded bg-amber-500/20">Primary Folder</span>
                    <h4 className="font-bold text-white text-xs mt-1">Official Chapter Google Drive Folder</h4>
                    <p className="text-[11px] text-slate-400 truncate max-w-md">{primaryDriveLink}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleAttachGDriveResource('Official Chapter Drive Folder', primaryDriveLink)}
                    className="px-3.5 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-black rounded-xl text-xs shadow hover:from-amber-600 transition flex items-center gap-1 cursor-pointer flex-shrink-0"
                  >
                    Attach Folder Link
                  </button>
                </div>
              )}

              {gdriveResources.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-60 overflow-y-auto pr-1">
                  {gdriveResources.map((r) => (
                    <div key={r.id} className="bg-slate-950 p-3.5 rounded-2xl border border-slate-800 flex flex-col justify-between gap-2">
                      <div>
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-bold text-amber-300 uppercase px-2 py-0.5 rounded bg-slate-900 border border-slate-800">{r.category}</span>
                          <span className="text-[9px] text-slate-500 font-bold">Drive Resource</span>
                        </div>
                        <h4 className="font-black text-white text-xs mt-1.5">{r.title}</h4>
                        {r.description && <p className="text-[10px] text-slate-400 line-clamp-2 mt-0.5">{r.description}</p>}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleAttachGDriveResource(r.title, r.link)}
                        className="w-full py-2 bg-slate-800 hover:bg-slate-700 text-amber-300 font-black rounded-xl text-xs transition border border-slate-700 flex items-center justify-center gap-1.5 cursor-pointer mt-1"
                      >
                        <span>Attach to ChatGPT</span> ↗
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-slate-950 p-6 rounded-2xl border border-slate-800 text-center text-slate-400 text-xs">
                  No additional Google Drive resources saved in Admin Studio yet. You can paste any shareable link above!
                </div>
              )}
            </div>

            {/* MODAL FOOTER */}
            <div className="flex items-center justify-end border-t border-slate-800 pt-4">
              <button
                type="button"
                onClick={() => setShowGDrivePickerModal(false)}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold rounded-xl text-xs transition cursor-pointer"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ChatGPT Admin Studio Component with Multi-Image & Multi-File Support
export default ChatGPTAdminStudio;
